/**
 * Declarative routing table: backend {@link ActivityEvent.type} → Redux
 * dispatch (PX-W6.1 skeleton, ported from xelerate-ai's
 * `services/websocket/EVENT_TYPE_MAP.ts`).
 *
 * Each entry returns the Redux action (string action-type + payload) to
 * dispatch; returning `null` is the explicit no-op. The keys must match the
 * literals in
 * `server/app/com/perpleks/px/api/infrastructure/events/ActivityEvents.java`
 * BYTE FOR BYTE — an unknown `event.type` is console.warn-ed and dropped, so a
 * typo here is not a compile error and not a runtime crash; it is a live
 * update that quietly never arrives. That is why a test asserts the literals
 * against the Java source rather than by eye (I-6).
 *
 * Every row must name an action a REGISTERED reducer handles (AC-9) — for
 * W6.1 that means the `realtime` slice's nonce/shell/echo reducers. Later
 * waves (W6.2–W6.5) add their rows beside these; do not remove a row without
 * removing its publisher.
 *
 * Refetch-on-invalidate only (I-9): entity frames upsert SHELLS (refetch
 * signals keyed by id), never merged server rows — a socket write path beside
 * the request write path is two sources of truth, and they disagree the first
 * time an event arrives out of order.
 */

import type { Action } from '@reduxjs/toolkit'
import type { RootState } from '@/store'
import type { ProjectSummary, ProjectsBusFrame } from '@/features/projects/types'
import type { VersionsBusFrame } from '@/features/versions/types'
import type { ActivityEvent } from './activityClient'

/**
 * Handler signature. `payload` is the event's body (unwrapped from the
 * {@link ActivityEvent} envelope). `getState` is passed so later waves can
 * apply scope guards.
 *
 * Return `null` to drop the event silently.
 */
export type EventHandler = (
  payload: Record<string, unknown>,
  getState: () => RootState
) => Action | null

// ---------------------------------------------------------------------------
// Payload helpers
// ---------------------------------------------------------------------------

function unwrapNotification(payload: Record<string, unknown>): Record<string, unknown> | null {
  const { notification } = payload
  if (notification !== undefined && notification !== null && typeof notification === 'object') {
    return notification as Record<string, unknown>
  }
  return null
}

/**
 * PX-W6.3: the `project.*` / `version.*` rows return the REALTIME NONCE
 * actions, now CARRYING the frame as their payload. The realtime slice's own
 * reducers ignore the payload (they only bump the nonce — which is what keeps
 * every row resolvable to a registered reducer, AC-9b), while the feature
 * slices `addCase` typed aliases of the SAME action type and read the frame.
 * That is the "two reducers, one event" fan-out: one dispatched action, no
 * change to the single-action map contract, no second dispatch path.
 */
function unwrapProject(payload: Record<string, unknown>): ProjectSummary | undefined {
  const { project } = payload
  if (project === undefined || project === null || typeof project !== 'object') return undefined
  return project as ProjectSummary
}

/** Narrow a frame field to `string | undefined`. */
const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined

/**
 * Build a `version.*` row: an ids-only frame on the versions nonce action.
 * Only `version.deleted` carries a top-level `projectId` (the project SLUG);
 * the row envelopes (`version.created|activated|archived`) carry the ObjectId
 * hex inside `version.projectId`, which is not the slug the client keys by —
 * so it stays unset and the slice treats "unknown project" as "invalidate".
 */
function versionFrame(event: VersionsBusFrame['event']): (payload: Record<string, unknown>) => Action {
  return (payload): Action => {
    const frame: VersionsBusFrame = {
      event,
      projectId: str(payload['projectId']),
      versionId: str(payload['versionId'])
    }
    return { type: 'realtime/invalidateVersions', payload: frame } as Action
  }
}

/**
 * Build the notifications-slice row upsert for a notification frame
 * (PX-W6.2): the payload is the FULL row as the server published it, and the
 * `notifications/notificationReceived` reducer merges it by id (echo-
 * idempotent) and recomputes the unread count from the rows. A frame whose
 * payload carries no resolvable notification object is dropped (null).
 */
function notificationRow(payload: Record<string, unknown>): Action | null {
  const notification = unwrapNotification(payload)
  if (notification === null) return null
  return { type: 'notifications/notificationReceived', payload: notification } as Action
}

// ---------------------------------------------------------------------------
// The map — W6.1 seed rows (spec §6.5). Every other backend literal is
// intentionally unmapped: unknown types warn + drop (I-6), later waves add
// their rows WITH their reducers.
// ---------------------------------------------------------------------------

export const EVENT_TYPE_MAP: Record<string, EventHandler> = {
  // ── Tasks → the board refetches (W6.3 owns the consuming surface) ────────
  'task.created': () => ({ type: 'realtime/invalidateBoard' }),
  'task.updated': () => ({ type: 'realtime/invalidateBoard' }),
  'task.deleted': () => ({ type: 'realtime/invalidateBoard' }),
  'task.status-changed': () => ({ type: 'realtime/invalidateBoard' }),

  // ── Projects → the portfolio + workspace surfaces (PX-W6.3: the nonce
  //    action now CARRIES the frame; the realtime nonce still bumps and the
  //    projects/projectDetail/phaseGates slices consume the payload) ─────────
  'project.updated': (payload): Action => {
    const frame: ProjectsBusFrame = { event: 'project.updated', project: unwrapProject(payload) }
    return { type: 'realtime/invalidateProjects', payload: frame } as Action
  },
  'project.archived': (payload): Action => {
    const frame: ProjectsBusFrame = { event: 'project.archived', project: unwrapProject(payload) }
    return { type: 'realtime/invalidateProjects', payload: frame } as Action
  },
  'project.deleted': (payload): Action => {
    const frame: ProjectsBusFrame = {
      event: 'project.deleted',
      projectId: str(payload['projectId'])
    }
    return { type: 'realtime/invalidateProjects', payload: frame } as Action
  },

  // ── Activity feed → page 1 refetches, project-scoped (PX-W6.3). The frame
  //    carries the activity row's project slug; a null projectId (user-level
  //    row, no project tuple) is carried as null and guarded in the slice —
  //    it never disturbs a mounted project feed (AC-5 negative) ──────────────
  'activity.created': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },

  // ── Versions → the work-package surfaces refetch (PX-W6.3: ids-only
  //    frames — refetch-on-invalidate, never a delta merge of the row) ──────
  'version.created': versionFrame('version.created'),
  'version.activated': versionFrame('version.activated'),
  'version.archived': versionFrame('version.archived'),
  'version.deleted': versionFrame('version.deleted'),

  // ── Notifications → the notifications slice's row upsert (PX-W6.2; the
  //    real consumer replaced the W6.1 skeleton shell — the reducer merges
  //    by id and recounts the unread state from the rows) ────────────────────
  'notification.created': notificationRow,
  'notification.updated': notificationRow,

  // ── Intakes (PX-W7.1) → the intakes slice refetches, project-scoped
  //    (`realtime/invalidateActivity` carries the frame's `projectId`, the
  //    same "two reducers, one event" fan-out `activity.created` uses) ──────
  'intake.created': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'intake.updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'intake.deleted': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'intake.session-updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'intake.conversation-assigned': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'intake.conversation-updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },

  // ── Requirements (PX-W7.2) ─────────────────────────────────────────────
  //    the requirements slice refetches, project-scoped
  //    (`realtime/invalidateActivity` carries the frame's `projectId`, the
  //    same "two reducers, one event" fan-out `activity.created` uses) ──────
  'requirement.created': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'requirement.updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'requirement.confirmed': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'requirement.rejected': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'requirement.superseded': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'requirement.bulk-imported': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },

  // ── Decisions (PX-W7.3) → the Blueprint decisions slice refetches,
  //    project-scoped. The decision payload remains ids-only; it is never
  //    merged into a row because the read projection enforces redaction. ────
  'decision.created': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'decision.updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'decision.decided': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },
  'decision.superseded': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    return { type: 'realtime/invalidateActivity', payload: { projectId } } as Action
  },

  // ── Migration (PX-W7.5) → the migration slice refetches, project-scoped
  //    (`realtime/invalidateActivity` carries the frame's `projectId` plus
  //    `planId`/`runId` when present, the same "two reducers, one event"
  //    fan-out `activity.created` uses) ─────────────────────────────────────
  'migration.plan.created': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.plan.scope.updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.plan.cutover-window.updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.plan.rollback-path.updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.plan.rollback.tested': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.plan.owner.updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.plan.status.transitioned': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.plan.deleted': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.run.started': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'migration.run.counts.updated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'migration.run.finished': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'reconciliation.report.generated': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'reconciliation.difference.owner-assigned': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'reconciliation.difference.proposed': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'reconciliation.difference.disposed': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'migration.go-no-go.recorded': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId } } as Action
  },
  'migration.rollback.armed': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'migration.rollback.executed': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'migration.rollback.released': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  },
  'migration.rollback.reminder': (payload): Action => {
    const projectId = typeof payload['projectId'] === 'string' ? payload['projectId'] : null
    const planId = typeof payload['planId'] === 'string' ? payload['planId'] : undefined
    const runId = typeof payload['runId'] === 'string' ? payload['runId'] : undefined
    return { type: 'realtime/invalidateActivity', payload: { projectId, planId, runId } } as Action
  }
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

/**
 * Route one inbound frame. The ECHO rule (I-7) runs FIRST, before the map: a
 * frame whose `actorUserId` is the local session user is recorded as
 * echo-suppressed instead of its normal action, so a frame for the user's own
 * write never re-applies on top of their thunk's own result.
 *
 * An unknown `type` warns and is dropped — never a crash (I-6).
 */
export function dispatchActivity(
  event: ActivityEvent,
  dispatch: (action: Action) => void,
  getState: () => RootState
): void {
  const payload = (event.payload ?? {}) as Record<string, unknown>

  if (event.actorUserId != null && event.actorUserId === getState().session.user?.id) {
    dispatch({ type: 'realtime/echoSuppressed' } as Action)
    return
  }

  const handler = EVENT_TYPE_MAP[event.type]
  if (handler === undefined) {
    console.warn('[ActivityClient] Unknown event type:', event.type)
    return
  }
  const action = handler(payload, getState)
  if (action !== null) {
    dispatch(action)
  }
}
